import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import {
  IFilter,
  IPopulate,
  IQueryParam,
  IProveedor,
  TipoProveedor,
  CategoriaProveedor,
  AccionesRol,
} from 'modelos/src';
import { Subscription } from 'rxjs';
import { ProveedoresService } from '../../../../auxiliares/servicios/http/proveedores.service';
import { MatDialogModule } from '@angular/material/dialog';
import { FormularioDialogService } from '../../../../auxiliares/servicios/formulario-dialog.service';
import { CrearEditarProveedorComponent } from '../crear-editar-proveedor/crear-editar-proveedor.component';
import {
  IColumnas,
  ICrearBoton,
  TablaComponent,
} from '../../../../auxiliares/tabla/tabla.component';
import {
  IFiltroTabla,
  IRegExpSearch,
} from '../../../../auxiliares/tabla/filtro/filtro.component';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { DialogService } from '../../../../auxiliares/dialog/dialog.service';
import { ActivosService } from '../../../../auxiliares/servicios/http/activos.service';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';

import { ActivatedRoute } from '@angular/router';
import { ClienteJerarquiaService } from '../../../../auxiliares/servicios/cliente-jerarquia.service';

@Component({
  selector: 'app-listado-proveedores',
  templateUrl: './listado-proveedores.html',
  styleUrl: './listado-proveedores.scss',
  standalone: true,
  imports: [TablaComponent, MatDialogModule],
})
export class ListadoProveedoresComponent implements OnInit, OnDestroy {
  helper = inject(HelperService);
  private clienteJerarquia = inject(ClienteJerarquiaService);
  private lista2 = inject(Lista2Service);
  private formularios = inject(FormularioDialogService);
  private service = inject(ProveedoresService);
  private dialogService = inject(DialogService);
  private route = inject(ActivatedRoute);

  //// Tabla nueva
  public categoria: CategoriaProveedor;
  public name = 'ListadoProveedoresComponent';
  public proveedores?: IProveedor[];
  public totalCount = 0;
  public columnas?: IColumnas<IProveedor>[];
  //Subs
  public proveedores$?: Subscription;
  //

  //Permisos
  private puedeCrear: AccionesRol;
  private puedeEditar: AccionesRol;
  private puedeEliminar: AccionesRol;

  public botonCrear: ICrearBoton;
  public search: IRegExpSearch = {
    fields: ['nombre'],
  };
  // FILTROS
  public cliente: IFiltroTabla = {
    filter: {
      field: 'idCliente',
    },
    label: 'Cliente',
    tipo: 'cliente-selector',
  };
  // Cliente en cuya jerarquía cuelga la entidad: el cliente elegido y toda su
  // descendencia.
  public ancestro: IFiltroTabla = {
    filter: {
      field: 'idsAncestros',
      incluirPropio: true,
    },
    label: 'En la jerarquía de',
    tipo: 'cliente-selector',
  };
  public filtros: IFiltroTabla[] = [];
  //Query

  public filter: IFilter<IProveedor> = {};
  public populate: IPopulate[] = [
    {
      path: 'cliente',
      select: 'nombre',
    },
  ];
  public query: IQueryParam = {
    filter: JSON.stringify(this.filter),
    populate: JSON.stringify(this.populate),
    sort: 'nombre',
    page: 0,
    limit: +this.helper.getPageSize(this.name),
    includeChildren: true,
  };

  // Listars

  public async listar(): Promise<void> {
    const filter: IFilter<IProveedor> = this.query.filter
      ? JSON.parse(this.query.filter)
      : {};
    filter.categoria = this.categoria;
    this.query.filter = JSON.stringify(filter);

    this.proveedores$?.unsubscribe();
    this.proveedores$ = this.lista2
      .proveedors(this.query, 30)
      .subscribe((data) => {
        this.totalCount = data.totalCount;
        this.proveedores = data.datos;
      });
  }

  // Acciones

  public crear() {
    const res = this.formularios.abrir(CrearEditarProveedorComponent, {
      tamano: 'mediano',
      data: {
        categoria: this.categoria,
      },
    });
    if (res) {
      res.afterClosed().subscribe((r) => {
        if (r) {
          this.listar();
        }
      });
    }
  }

  public crearProveedor() {
    const dialog = this.formularios.abrir(CrearEditarProveedorComponent, {
      tamano: 'mediano',
      data: {
        categoria: this.categoria,
      },
    });
    dialog.afterClosed().subscribe((res) => {
      if (res) {
        this.listar();
      }
    });
  }

  public async editar(proveedor: IProveedor) {
    const dialog = this.formularios.abrir(CrearEditarProveedorComponent, {
      tamano: 'mediano',
      data: {
        edit: proveedor,
        categoria: this.categoria,
      },
    });
    dialog.afterClosed().subscribe((res) => {
      if (res) {
        this.listar();
      }
    });
  }

  public async eliminar(proveedor: IProveedor) {
    const res = await this.dialogService.confirm(
      'Eliminar proveedor',
      `¿Querés eliminar el proveedor ${proveedor.nombre}?`,
    );
    if (!res) return;
    try {
      await this.service.delete(proveedor._id!);
      this.helper.notifSuccess('Proveedor eliminado');
      await this.listar();
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
  }

  // Tabla

  private setColumnas() {
    this.columnas = [
      {
        header: {
          label: 'Tipos',
        },
        row: {
          parse(dato) {
            // Si es un array de tipos, lo mapeamos y unimos con coma
            return dato.tipos
              ?.map((t: TipoProveedor) => (t === 'Mecanico' ? 'Mecánico' : t))
              .join(', ');
          },
          noData: 'Sin tipos',
        },
      },
      {
        header: {
          label: 'Nombre',
          sortable: true,
        },
        row: {
          field: 'nombre',
          noData: 'Sin nombre',
        },
      },
    ];
    // Cliente
    if (HelperService.getTipoCliente() !== 'Final') {
      {
        this.columnas.push({
          header: {
            label: 'Cliente',
          },
          row: {
            html: (dato) => this.clienteJerarquia.htmlNombre(dato.cliente),
            tooltip: (dato) =>
              this.clienteJerarquia.tooltipJerarquia(dato.cliente),
            noData: 'Sin cliente',
          },
        });
      }
    }

    this.columnas.push({
      header: {
        label: 'Acciones',
      },
      row: {
        acciones: [
          {
            tipo: 'material',
            color: 'primary',
            icon: 'edit',
            tooltip: 'Editar',
            click: (dato) => this.editar(dato),
            disabled: () => !this.helper.tienePermiso(this.puedeEditar),
          },
          {
            tipo: 'material',
            color: 'warn',
            icon: 'delete',
            tooltip: 'Eliminar',
            click: (dato) => this.eliminar(dato),
            disabled: () => !this.helper.tienePermiso(this.puedeEliminar),
          },
        ],
      },
    });
  }

  // Hooks

  async ngOnInit() {
    if (HelperService.getTipoCliente() !== 'Final') {
      this.filtros.push(this.cliente);
      this.filtros.push(this.ancestro);
    }
    this.route.params.subscribe(async (params) => {
      this.categoria = params['categoria'] as CategoriaProveedor;

      switch (this.categoria) {
        case 'Colectivo':
          this.puedeCrear = 'Colectivos - Crear proveedores';
          this.puedeEditar = 'Colectivos - Editar proveedores';
          this.puedeEliminar = 'Colectivos - Eliminar proveedores';
          break;
        case 'Vehiculo':
          this.puedeCrear = 'Vehículos - Crear proveedores';
          this.puedeEditar = 'Vehículos - Editar proveedores';
          this.puedeEliminar = 'Vehículos - Eliminar proveedores';
          break;
      }

      // Inicializo el botón ahora que ya tengo los permisos
      this.botonCrear = {
        mostrar: this.helper.tienePermiso(this.puedeCrear),
        tooltip: 'Crear proveedor',
        color: 'primary',
        tipo: 'material',
        icon: 'add',
        accion: () => this.crear(),
      };
      await this.listar();
    });

    this.setColumnas();
  }

  async ngOnDestroy() {
    this.proveedores$?.unsubscribe();
  }
}
